import { Alert, Paper } from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AutotractorSlotIDs } from "common/components/map/layers/types";
import { CarbonLoadingSpinner } from "portal/components/CarbonLoadingSpinner";
import { FarmMapData, getFieldBounds } from "common/utils/farm";
import { Job } from "protos/rtc/jobs";
import { Map } from "portal/components/map/Map";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useFarmLayers } from "common/components/map/layers/FarmLayers";
import { useMapSlots } from "common/components/map/MapSlots";
import { useObjectiveLayers } from "common/components/map/layers/ObjectiveLayers";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTaskLayers } from "common/components/map/layers/TaskLayers";
import { useTranslation } from "react-i18next";
import { viteImageResolver } from "common/utils/map/imageResolver.vite";
import React from "react";

interface DryRunPreviewProps {
  dryRun: Job;
  farmMapData: FarmMapData | undefined;
  showJobData: "objective" | "task";
  configValueDisplayModules: JSX.Element[];
}

export const DryRunPreview: React.FC<DryRunPreviewProps> = ({
  dryRun,
  farmMapData,
  showJobData,
  configValueDisplayModules,
}) => {
  const { t } = useTranslation();
  const { data: configuredJob, isLoading: configuredJobLoading } =
    useQueryPopups(
      rtcJobsApi.useGetConfiguredJobQuery(
        { id: dryRun.id.toString() },
        { skip: dryRun.creationFailed }
      )
    );
  const { data: listTasksResponse, isLoading: tasksLoading } = useQueryPopups(
    rtcJobsApi.useListTasksQuery(
      {
        objectiveIds: (configuredJob?.job.objectives ?? []).map(
          (obj) => obj.id
        ),
      },
      { skip: dryRun.creationFailed }
    )
  );
  const { data: rigResp } = atkEquipmentApi.endpoints.getRig.useQuery(
    { id: configuredJob?.groundPrep?.rigId ?? "", validate: true },
    { skip: !configuredJob?.groundPrep?.rigId }
  );
  const rig = rigResp?.validationResult?.effectiveConfig;

  const { Layers: FarmLayers, idsToLoad: farmIdsToLoad } = useFarmLayers({});
  const { Layers: ObjectiveLayers, idsToLoad: objectiveIdsToLoad } =
    useObjectiveLayers({});
  const { Layers: TaskLayers, idsToLoad: taskIdsToLoad } = useTaskLayers({});
  const mapSlots = useMapSlots(Object.values(AutotractorSlotIDs));
  const field = dryRun.fieldId
    ? farmMapData?.zonesById[dryRun.fieldId]
    : undefined;
  const fieldBounds =
    field && farmMapData
      ? getFieldBounds(field, farmMapData.pointsById)
      : undefined;

  if (configuredJobLoading || tasksLoading) {
    return <CarbonLoadingSpinner className="justify-center" />;
  } else if (!dryRun.creationFailed && configuredJob) {
    return (
      <Paper
        variant="outlined"
        className="bg-inherit flex flex-row p-4 gap-4 rounded-lg"
      >
        <Map
          className="h-[25rem]"
          allowEmpty
          hideHoverInfo
          extraBounds={fieldBounds}
          imageConfig={{
            resolver: viteImageResolver,
            idsToLoad: [
              ...farmIdsToLoad,
              ...objectiveIdsToLoad,
              ...taskIdsToLoad,
            ],
          }}
          extraControls={
            <>
              {mapSlots}
              {farmMapData && (
                <FarmLayers
                  beforeId={AutotractorSlotIDs.FARMS}
                  shownPoints={[]}
                  selectedZoneIds={[]}
                  focusedZoneIds={[]}
                  hidePivots
                  farmsMapData={[farmMapData]}
                />
              )}
              {showJobData === "objective" && (
                <ObjectiveLayers
                  configuredJob={configuredJob}
                  objectives={configuredJob.job.objectives}
                  focusedObjective={undefined}
                  selectedObjective={undefined}
                  focusedTractor={undefined}
                  highlightedObjectives={[]}
                  hiddenTractors={[]}
                  assignmentsByTractorSerial={{}}
                  colorsBySerial={{}}
                  beforeId={AutotractorSlotIDs.JOB_OBJECTIVES}
                />
              )}
              {showJobData === "task" && (
                <TaskLayers
                  configuredJob={configuredJob}
                  beforeId={AutotractorSlotIDs.JOB_TASKS}
                  tasks={listTasksResponse?.tasks ?? []}
                  showTasks={true}
                  rig={rig}
                  followPathDisplay="implement"
                />
              )}
            </>
          }
        />
        {configValueDisplayModules.length > 0 && (
          <div className="flex flex-col justify-end w-1/6">
            {configValueDisplayModules}
          </div>
        )}
      </Paper>
    );
  } else if (dryRun.creationFailed) {
    // error generating path plan
    return (
      <Alert severity="warning" className="w-full text-base rounded-lg">
        {t(
          "views.autotractor.jobCreation.step3ConfigurePreview.serviceFailureAlert"
        )}
      </Alert>
    );
  }
};
